import { FC, useMemo, type CSSProperties } from 'react'
import { Image } from 'antd'
import type { CrmIntegrationAttachment } from '../../types/crm-integration-chat.types'
import { partitionCrmIntegrationAttachments } from '../../lib/crmIntegrationAttachment'
import { getPhotoGridColumnCount } from '../../lib/crmIntegrationPhotoGrid'
import CrmIntegrationIncomingFile from './CrmIntegrationIncomingFile'
import CrmIntegrationIncomingImage from './CrmIntegrationIncomingImage'
import CrmIntegrationIncomingVideo from './CrmIntegrationIncomingVideo'
import CrmIntegrationIncomingVoice from './CrmIntegrationIncomingVoice'
import CrmIntegrationIncomingAudio from './CrmIntegrationIncomingAudio'
import CrmIntegrationIncomingUnsupportedAttachment from './CrmIntegrationIncomingUnsupportedAttachment'
import classes from '../../styles/crmIntegrationChat.module.css'

type CrmIntegrationIncomingAttachmentsProps = {
  attachments: CrmIntegrationAttachment[]
  onVideoPlaybackError?: () => void
  onVoicePlaybackError?: () => void
}

const CrmIntegrationIncomingAttachments: FC<
  CrmIntegrationIncomingAttachmentsProps
> = ({ attachments, onVideoPlaybackError, onVoicePlaybackError }) => {
  const { images, files, videos, voices, audios, unsupported } = useMemo(
    () => partitionCrmIntegrationAttachments(attachments),
    [attachments],
  )

  const photoCount = images.length
  const gridStyle = useMemo(
    () =>
      ({
        '--photo-grid-columns': getPhotoGridColumnCount(photoCount),
      }) as CSSProperties,
    [photoCount],
  )

  if (
    images.length === 0 &&
    files.length === 0 &&
    videos.length === 0 &&
    voices.length === 0 &&
    audios.length === 0 &&
    unsupported.length === 0
  ) {
    return null
  }

  const isSinglePhoto = photoCount === 1

  return (
    <>
      {photoCount > 0 ? (
        <Image.PreviewGroup>
          <div
            className={classes.bubbleAttachments}
            data-photo-count={photoCount}
            style={gridStyle}
          >
            {images.map((att, index) => (
              <CrmIntegrationIncomingImage
                key={`${att.provider_meta?.file_id ?? att.url ?? index}-${index}`}
                att={att}
                photoCount={photoCount}
                index={index}
                isSinglePhoto={isSinglePhoto}
              />
            ))}
          </div>
        </Image.PreviewGroup>
      ) : null}
      {files.map((att, index) => (
        <CrmIntegrationIncomingFile
          key={`file-${att.provider_meta?.file_id ?? att.url ?? att.name ?? index}-${index}`}
          att={att}
        />
      ))}
      {videos.map((att, index) => (
        <CrmIntegrationIncomingVideo
          key={`video-${att.provider_meta?.file_id ?? att.url ?? att.name ?? index}-${index}`}
          att={att}
          onPlaybackError={onVideoPlaybackError}
        />
      ))}
      {audios.map((att, index) => (
        <CrmIntegrationIncomingAudio
          key={`audio-${att.provider_meta?.id ?? att.url ?? att.name ?? index}-${index}`}
          att={att}
        />
      ))}
      {voices.map((att, index) => (
        <CrmIntegrationIncomingVoice
          key={`voice-${att.provider_meta?.file_id ?? att.url ?? att.name ?? index}-${index}`}
          att={att}
          onPlaybackError={onVoicePlaybackError}
        />
      ))}
      {unsupported.map((att, index) => (
        <CrmIntegrationIncomingUnsupportedAttachment
          key={`unsupported-${att.kind}-${att.provider_meta?.file_id ?? att.url ?? att.name ?? index}-${index}`}
          att={att}
        />
      ))}
    </>
  )
}

export default CrmIntegrationIncomingAttachments
